import type {
  PrChatContextFile,
  PrChatRetrievalContext,
  PrChatThread,
} from '../../pr-context/types';
import type {
  ChangedFileMatchKind,
  PrContextToolErrorOutput,
  PrThreadSummary,
  TruncatedTextArtifact,
} from './types';

import { SEARCH_SNIPPET_RADIUS, TEXT_ARTIFACT_MAX_LENGTH } from './constants';

export function createErrorOutput(error: string): PrContextToolErrorOutput {
  // Missing user-level inputs should become tool data, not thrown exceptions.
  return {
    success: false,
    error,
    summary: error,
  };
}

export function clampLimit(
  value: number | undefined,
  defaultLimit: number,
  maxLimit: number,
): number {
  if (value === undefined) {
    return defaultLimit;
  }

  return Math.min(Math.max(Math.trunc(value), 1), maxLimit);
}

export function truncateArtifact(path: string, content: string): TruncatedTextArtifact {
  // Tool results are rendered generically today, so large artifacts need a hard cap.
  const truncated = content.length > TEXT_ARTIFACT_MAX_LENGTH;
  return {
    path,
    content: truncated ? content.slice(0, TEXT_ARTIFACT_MAX_LENGTH) : content,
    originalLength: content.length,
    truncated,
  };
}

export function summarizeThread(thread: PrChatThread): PrThreadSummary {
  return {
    id: thread.id,
    headline: thread.headline,
    locationLabel: thread.locationLabel,
    path: thread.path,
    line: thread.line,
    startLine: thread.startLine,
    endLine: thread.endLine,
    isResolved: thread.isResolved,
    isOutdated: thread.isOutdated,
    headStatus: thread.headStatus,
    commentCount: thread.commentCount,
    lastActivityAt: thread.lastActivityAt,
  };
}

export function resolveChangedFilePathFromOrder(
  fileOrder: readonly string[],
  requestedPath: string,
  filesByPath?: Record<string, unknown>,
): {
  path: string;
  matchKind: ChangedFileMatchKind;
} | null {
  const query = requestedPath.trim();
  if (!query) {
    return null;
  }

  if (filesByPath ? filesByPath[query] : fileOrder.includes(query)) {
    return { path: query, matchKind: 'exact' };
  }

  // Resolution intentionally goes from safest to most forgiving so fuzzy matches
  // never hide an exact path when two filenames are similar.
  const lowerQuery = query.toLowerCase();
  const caseInsensitivePath = fileOrder.find((path) => path.toLowerCase() === lowerQuery);
  if (caseInsensitivePath) {
    return { path: caseInsensitivePath, matchKind: 'case-insensitive' };
  }

  const basenamePath = fileOrder.find(
    (path) => path.split('/').pop()?.toLowerCase() === lowerQuery,
  );
  if (basenamePath) {
    return { path: basenamePath, matchKind: 'basename' };
  }

  const fuzzyPath = fileOrder.find(
    (path) => path.toLowerCase().includes(lowerQuery) || lowerQuery.includes(path.toLowerCase()),
  );
  return fuzzyPath ? { path: fuzzyPath, matchKind: 'fuzzy' } : null;
}

export function resolveChangedFilePath(
  context: PrChatRetrievalContext,
  requestedPath: string,
): {
  path: string;
  matchKind: ChangedFileMatchKind;
} | null {
  return resolveChangedFilePathFromOrder(context.fileOrder, requestedPath, context.filesByPath);
}

export function findFileArtifact(
  context: PrChatRetrievalContext,
  filePath: string,
  suffix: '/context.json' | '/diff.patch',
): PrChatContextFile | null {
  // Artifacts are already materialized in retrieval context; this tool never
  // reaches into the filesystem or GitHub to satisfy a read.
  return (
    context.contextFiles.find(
      (file) => file.kind === 'file' && file.relatedPath === filePath && file.path.endsWith(suffix),
    ) ?? null
  );
}

export function buildSearchSnippet(file: PrChatContextFile, query: string): string {
  const content = file.content || file.title || file.path;
  const lowerContent = content.toLowerCase();
  const index = lowerContent.indexOf(query.toLowerCase());

  if (index === -1) {
    return content
      .slice(0, SEARCH_SNIPPET_RADIUS * 2)
      .replace(/\s+/g, ' ')
      .trim();
  }

  const start = Math.max(0, index - SEARCH_SNIPPET_RADIUS);
  const end = Math.min(content.length, index + query.length + SEARCH_SNIPPET_RADIUS);
  const prefix = start > 0 ? '...' : '';
  const suffix = end < content.length ? '...' : '';
  return `${prefix}${content.slice(start, end)}${suffix}`.replace(/\s+/g, ' ').trim();
}

export function fileMatchesQuery(file: PrChatContextFile, query: string): boolean {
  // `searchText` is the preferred normalized corpus, but older/generated
  // artifacts can have it empty, so fall back through visible metadata.
  const lowerQuery = query.toLowerCase();
  return (
    file.searchText.includes(lowerQuery) ||
    file.content.toLowerCase().includes(lowerQuery) ||
    file.title.toLowerCase().includes(lowerQuery) ||
    file.path.toLowerCase().includes(lowerQuery) ||
    Boolean(file.relatedPath?.toLowerCase().includes(lowerQuery))
  );
}
